import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import {
  WhatsApp,
  Instagram,
  Facebook,
  Refresh,
  CheckCircle,
  FileCopy,
  Add,
  Close as CloseIcon,
  Send as SendIcon,
} from "@material-ui/icons";
import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const NotificameHub = () => {
  const [loading, setLoading] = useState(false);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [channels, setChannels] = useState([]);
  const [hasToken, setHasToken] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const [existingConnections, setExistingConnections] = useState([]);

  // Modal para criar conexão
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [connectionName, setConnectionName] = useState("");
  const [connectionToken, setConnectionToken] = useState("");
  const [creatingConnection, setCreatingConnection] = useState(false);

  // Modal de teste de integração
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testConnection, setTestConnection] = useState(null);
  const [testNumber, setTestNumber] = useState("");
  const [testMessage, setTestMessage] = useState("🧪 Teste de integração NotificaMe Hub");
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    fetchTokenStatus();
    fetchExistingConnections();
  }, []);

  const fetchTokenStatus = async () => {
    try {
      const { data } = await api.get("/notificamehub/token");
      setHasToken(data.hasToken);
      if (data.hasToken) {
        setTokenValid(true);
      }
    } catch (err) {
      console.error("Erro ao buscar status do token:", err);
    }
  };

  const fetchExistingConnections = async () => {
    try {
      const { data } = await api.get("/whatsapp?session=0");
      const notificamehubConnections = data.filter(
        (w) => w.provider === "notificamehub"
      );
      setExistingConnections(notificamehubConnections);
    } catch (err) {
      console.error("Erro ao buscar conexões existentes:", err);
    }
  };

  const fetchChannels = useCallback(async () => {
    setLoadingChannels(true);
    try {
      const { data } = await api.get("/notificamehub/channels");
      setChannels(data || []);
      setTokenValid(true);
    } catch (err) {
      console.error("Erro ao buscar canais:", err);
      if (err.response?.status === 400) {
        toast.warning("Configure o token NotificameHub primeiro");
      } else if (err.response?.status === 401) {
        setTokenValid(false);
        toast.error("Token inválido ou expirado");
      }
      setChannels([]);
    } finally {
      setLoadingChannels(false);
    }
  }, []);

  useEffect(() => {
    if (hasToken && tokenValid) {
      fetchChannels();
    }
  }, [hasToken, tokenValid, fetchChannels]);

  const handleSaveToken = async (tokenValue) => {
    if (!tokenValue.trim()) {
      toast.error("Insira um token válido");
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post("/notificamehub/token", { token: tokenValue.trim() });
      
      if (data.connectionsUpdated > 0) {
        toast.success(`Token salvo! ${data.connectionsUpdated} conexões atualizadas.`);
      } else {
        toast.success("Token salvo com sucesso!");
      }
      
      setHasToken(true);
      setTokenValid(true);
      fetchChannels();
      fetchExistingConnections();
    } catch (err) {
      toastError(err);
      setTokenValid(false);
    } finally {
      setLoading(false);
    }
  };

  const getChannelIcon = (type) => {
    const iconStyle = { fontSize: "48px" };
    switch (type) {
      case "whatsapp":
        return <WhatsApp style={{ ...iconStyle, color: "#25D366" }} />;
      case "instagram":
        return <Instagram style={{ ...iconStyle, color: "#E4405F" }} />;
      case "facebook":
        return <Facebook style={{ ...iconStyle, color: "#1877F2" }} />;
      default:
        return <WhatsApp style={iconStyle} />;
    }
  };

  const getChannelTypeName = (type) => {
    switch (type) {
      case "whatsapp":
        return "WhatsApp";
      case "instagram":
        return "Instagram";
      case "facebook":
        return "Facebook Messenger";
      case "telegram":
        return "Telegram";
      default:
        return type;
    }
  };

  const isChannelConnected = (channelId) => {
    return existingConnections.some(
      (conn) => conn.notificamehubChannelId === channelId
    );
  };

  const handleOpenCreateModal = (channel) => {
    setSelectedChannel(channel);
    setConnectionName(channel.name || `${getChannelTypeName(channel.type)} - NotificameHub`);
    setConnectionToken("");
    setCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
    setSelectedChannel(null);
    setConnectionName("");
    setConnectionToken("");
  };

  const handleCreateConnection = async () => {
    if (!connectionName.trim()) {
      toast.error("Insira um nome para a conexão");
      return;
    }

    if (!connectionToken.trim()) {
      toast.error("Insira o token NotificameHub");
      return;
    }

    setCreatingConnection(true);
    try {
      const channelType = selectedChannel.type || selectedChannel.channel || "whatsapp";

      const response = await api.post("/whatsapp", {
        name: connectionName.trim(),
        channel: channelType,
        provider: "notificamehub",
        notificamehubChannelId: selectedChannel.id,
        notificamehubToken: connectionToken.trim(),
        isDefault: false,
        greetingMessage: "",
        farewellMessage: "",
        status: "CONNECTED",
      });

      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin.replace(':3000', ':8080');
      const newWhatsappId = response.data?.id || response.data?.whatsapp?.id;
      const webhookUrl = `${backendUrl}/webhooks/notificamehub/${newWhatsappId}`;

      toast.success(`Conexão criada! Webhook: ${webhookUrl}`, { autoClose: 10000 });

      handleCloseCreateModal();
      fetchExistingConnections();
    } catch (err) {
      toastError(err);
    } finally {
      setCreatingConnection(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado para área de transferência!");
  };

  const handleOpenTestModal = (connection) => {
    setTestConnection(connection);
    setTestNumber("");
    setTestMessage("🧪 Teste de integração NotificaMe Hub");
    setTestModalOpen(true);
  };

  const handleCloseTestModal = () => {
    setTestModalOpen(false);
    setTestConnection(null);
    setTestNumber("");
    setTestMessage("🧪 Teste de integração NotificaMe Hub");
  };

  const handleSendTest = async () => {
    if (!testNumber.trim()) {
      toast.error("Insira um número de destino");
      return;
    }

    if (!testMessage.trim()) {
      toast.error("Insira uma mensagem");
      return;
    }

    setSendingTest(true);
    try {
      const response = await api.post("/notificamehub/test-send", {
        whatsappId: testConnection.id,
        to: testNumber.trim(),
        message: testMessage.trim(),
      });

      toast.success(`✅ Mensagem enviada! ID: ${response.data.messageId}`);
      handleCloseTestModal();
    } catch (err) {
      toastError(err);
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <MainContainer>
      <div style={{
        padding: "24px",
        background: "#f5f7fa",
        minHeight: "calc(100vh - 64px)",
        overflowY: "auto",
      }}>
        {/* Header */}
        <div style={{ marginBottom: "24px" }}>
          <h1 style={{
            fontSize: "24px",
            fontWeight: 700,
            color: "#1a1a1a",
            margin: 0,
          }}>
            🔔 NotificameHub
          </h1>
          <p style={{
            fontSize: "14px",
            color: "#666",
            margin: "8px 0 0 0",
          }}>
            Configure canais de comunicação WhatsApp, Instagram e Facebook
          </p>
        </div>

        {/* Botão Atualizar */}
        <div style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "24px",
        }}>
          <button
            onClick={fetchChannels}
            disabled={!hasToken || loadingChannels}
            style={{
              padding: "12px 20px",
              background: "#e8ecf1",
              color: "#1a1a1a",
              border: "none",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: 600,
              cursor: (!hasToken || loadingChannels) ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              opacity: (!hasToken || loadingChannels) ? 0.6 : 1,
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              if (hasToken && !loadingChannels) {
                e.currentTarget.style.background = "#d1d5db";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#e8ecf1";
            }}
          >
            <Refresh style={{ fontSize: "18px" }} />
            Atualizar
          </button>
        </div>

        {/* Channels Grid */}
        {loadingChannels ? (
          <div style={{
            textAlign: "center",
            padding: "60px 20px",
            background: "white",
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: "1px solid #e8ecf1",
          }}>
            <div style={{
              width: "40px",
              height: "40px",
              border: "4px solid #e8ecf1",
              borderTop: "4px solid #3b82f6",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 16px",
            }} />
            <p style={{ color: "#666", fontSize: "14px" }}>Carregando canais...</p>
          </div>
        ) : channels.length > 0 ? (
          <>
            <div style={{
              fontSize: "18px",
              fontWeight: 600,
              color: "#1a1a1a",
              marginBottom: "16px",
            }}>
              📱 Canais Disponíveis ({channels.length})
            </div>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: "16px",
              marginBottom: "32px",
            }}>
              {channels.map((channel) => {
                const connected = isChannelConnected(channel.id);
                return (
                  <div
                    key={channel.id}
                    style={{
                      background: "white",
                      borderRadius: "12px",
                      padding: "20px",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                      border: "1px solid #e8ecf1",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-4px)";
                      e.currentTarget.style.boxShadow = "0 8px 16px rgba(0,0,0,0.12)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.08)";
                    }}
                  >
                    <div style={{ textAlign: "center", marginBottom: "16px" }}>
                      {getChannelIcon(channel.type)}
                    </div>
                    <div style={{
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "#1a1a1a",
                      marginBottom: "8px",
                      textAlign: "center",
                    }}>
                      {channel.name || getChannelTypeName(channel.type)}
                    </div>
                    {channel.phone && (
                      <div style={{
                        fontSize: "13px",
                        color: "#666",
                        marginBottom: "12px",
                        textAlign: "center",
                      }}>
                        📞 {channel.phone}
                      </div>
                    )}
                    <div style={{
                      display: "flex",
                      justifyContent: "center",
                      marginBottom: "16px",
                    }}>
                      <span style={{
                        padding: "4px 12px",
                        borderRadius: "20px",
                        fontSize: "12px",
                        fontWeight: 600,
                        background: connected ? "#d4edda" : "#dbeafe",
                        color: connected ? "#155724" : "#1e40af",
                      }}>
                        {connected ? "✓ Conectado" : "Disponível"}
                      </span>
                    </div>
                    <button
                      onClick={() => handleOpenCreateModal(channel)}
                      disabled={connected}
                      style={{
                        width: "100%",
                        padding: "10px",
                        background: connected ? "#e8ecf1" : "#3b82f6",
                        color: connected ? "#999" : "white",
                        border: "none",
                        borderRadius: "8px",
                        fontSize: "13px",
                        fontWeight: 600,
                        cursor: connected ? "not-allowed" : "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                      }}
                    >
                      <Add style={{ fontSize: "18px" }} />
                      {connected ? "Já Conectado" : "Conectar Canal"}
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        ) : hasToken ? (
          <div style={{
            textAlign: "center",
            padding: "60px 20px",
            background: "white",
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            border: "1px solid #e8ecf1",
            color: "#999",
          }}>
            <p>Nenhum canal encontrado. Verifique se você tem canais configurados no NotificameHub.</p>
          </div>
        ) : null}

        {/* Existing Connections */}
        {existingConnections.length > 0 && (
          <>
            <div style={{
              fontSize: "18px",
              fontWeight: 600,
              color: "#1a1a1a",
              marginBottom: "16px",
              marginTop: "32px",
            }}>
              🔗 Conexões Ativas ({existingConnections.length})
            </div>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
              gap: "16px",
            }}>
              {existingConnections.map((conn) => {
                const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin.replace(':3000', ':8080');
                const webhookUrl = `${backendUrl}/webhooks/notificamehub/${conn.id}`;

                return (
                  <div
                    key={conn.id}
                    style={{
                      background: "white",
                      borderRadius: "12px",
                      padding: "20px",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                      border: "1px solid #e8ecf1",
                    }}
                  >
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      marginBottom: "12px",
                    }}>
                      {getChannelIcon(conn.channel)}
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: "16px",
                          fontWeight: 600,
                          color: "#1a1a1a",
                        }}>
                          {conn.name}
                        </div>
                        <div style={{
                          fontSize: "12px",
                          color: "#666",
                        }}>
                          {getChannelTypeName(conn.channel)}
                        </div>
                      </div>
                      <span style={{
                        padding: "4px 12px",
                        borderRadius: "20px",
                        fontSize: "11px",
                        fontWeight: 600,
                        background: conn.status === "CONNECTED" ? "#d4edda" : "#fef3c7",
                        color: conn.status === "CONNECTED" ? "#155724" : "#92400e",
                      }}>
                        {conn.status}
                      </span>
                    </div>
                    <div style={{
                      background: "#f9fafb",
                      border: "1px solid #e8ecf1",
                      borderRadius: "6px",
                      padding: "12px",
                      marginTop: "12px",
                    }}>
                      <div style={{
                        fontSize: "11px",
                        fontWeight: 600,
                        color: "#666",
                        marginBottom: "6px",
                      }}>
                        Webhook URL:
                      </div>
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}>
                        <code style={{
                          flex: 1,
                          fontSize: "11px",
                          color: "#1a1a1a",
                          background: "white",
                          padding: "6px 8px",
                          borderRadius: "4px",
                          border: "1px solid #e8ecf1",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}>
                          {webhookUrl}
                        </code>
                        <button
                          onClick={() => copyToClipboard(webhookUrl)}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "4px",
                            display: "flex",
                            alignItems: "center",
                          }}
                          title="Copiar"
                        >
                          <FileCopy style={{ fontSize: "16px", color: "#3b82f6" }} />
                        </button>
                      </div>
                      <div style={{
                        fontSize: "11px",
                        color: "#666",
                        marginTop: "8px",
                      }}>
                        Configure esta URL no painel do NotificameHub
                      </div>
                    </div>
                    <button
                      onClick={() => handleOpenTestModal(conn)}
                      style={{
                        width: "100%",
                        padding: "10px",
                        background: "#10b981",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        fontSize: "13px",
                        fontWeight: 600,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                        marginTop: "12px",
                      }}
                    >
                      <SendIcon style={{ fontSize: "18px" }} />
                      Testar Integração
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Test Integration Modal */}
        {testModalOpen && (
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}>
            <div style={{
              background: "white",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "500px",
              width: "90%",
              maxHeight: "90vh",
              overflowY: "auto",
            }}>
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}>
                <h2 style={{ margin: 0, fontSize: "20px", fontWeight: 600 }}>
                  🧪 Testar Integração
                </h2>
                <button
                  onClick={handleCloseTestModal}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "4px",
                  }}
                >
                  <CloseIcon />
                </button>
              </div>

              <div style={{ marginBottom: "16px" }}>
                <div style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  marginBottom: "8px",
                  color: "#1a1a1a",
                }}>
                  Conexão:
                </div>
                <div style={{
                  padding: "12px",
                  background: "#f9fafb",
                  borderRadius: "8px",
                  border: "1px solid #e8ecf1",
                }}>
                  <div style={{ fontWeight: 600 }}>{testConnection?.name}</div>
                  <div style={{ fontSize: "12px", color: "#666" }}>
                    {getChannelTypeName(testConnection?.channel)}
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: 600,
                  marginBottom: "8px",
                  color: "#1a1a1a",
                }}>
                  Número de Destino (com DDI):
                </label>
                <input
                  type="text"
                  value={testNumber}
                  onChange={(e) => setTestNumber(e.target.value)}
                  placeholder="Ex: 5547999999999"
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #e8ecf1",
                    borderRadius: "8px",
                    fontSize: "14px",
                    boxSizing: "border-box",
                  }}
                />
                <div style={{
                  fontSize: "12px",
                  color: "#666",
                  marginTop: "4px",
                }}>
                  Inclua o código do país (DDI). Ex: 55 para Brasil
                </div>
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: 600,
                  marginBottom: "8px",
                  color: "#1a1a1a",
                }}>
                  Mensagem:
                </label>
                <textarea
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  rows={4}
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #e8ecf1",
                    borderRadius: "8px",
                    fontSize: "14px",
                    boxSizing: "border-box",
                    resize: "vertical",
                  }}
                />
              </div>

              <div style={{
                display: "flex",
                gap: "12px",
              }}>
                <button
                  onClick={handleCloseTestModal}
                  disabled={sendingTest}
                  style={{
                    flex: 1,
                    padding: "12px",
                    background: "#e8ecf1",
                    color: "#1a1a1a",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: 600,
                    cursor: sendingTest ? "not-allowed" : "pointer",
                  }}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSendTest}
                  disabled={sendingTest}
                  style={{
                    flex: 1,
                    padding: "12px",
                    background: sendingTest ? "#9ca3af" : "#10b981",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: 600,
                    cursor: sendingTest ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <SendIcon style={{ fontSize: "18px" }} />
                  {sendingTest ? "Enviando..." : "Enviar Teste"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create Connection Modal */}
        {createModalOpen && (
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "20px",
          }}>
            <div
              style={{
                background: "white",
                borderRadius: "12px",
                padding: "32px",
                maxWidth: "500px",
                width: "100%",
                maxHeight: "90vh",
                overflowY: "auto",
                boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "24px",
              }}>
                <h2 style={{
                  fontSize: "20px",
                  fontWeight: 700,
                  color: "#1a1a1a",
                  margin: 0,
                }}>
                  🔗 Criar Conexão
                </h2>
                <button
                  onClick={handleCloseCreateModal}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "4px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <CloseIcon style={{ fontSize: "20px", color: "#6b7280" }} />
                </button>
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label style={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#374151",
                  display: "block",
                  marginBottom: "8px",
                }}>
                  Nome da Conexão
                </label>
                <input
                  type="text"
                  value={connectionName}
                  onChange={(e) => setConnectionName(e.target.value)}
                  placeholder="Ex: WhatsApp - Atendimento"
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                  }}
                />
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label style={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#374151",
                  display: "block",
                  marginBottom: "8px",
                }}>
                  Token NotificameHub
                </label>
                <input
                  type="password"
                  value={connectionToken}
                  onChange={(e) => setConnectionToken(e.target.value)}
                  placeholder="Insira o token da API"
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontFamily: "inherit",
                  }}
                />
                <div style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  marginTop: "6px",
                }}>
                  Token necessário para conectar com a API
                </div>
              </div>

              {selectedChannel?.phone && (
                <div style={{
                  background: "#f0f9ff",
                  border: "1px solid #bae6fd",
                  borderRadius: "8px",
                  padding: "12px",
                  marginBottom: "20px",
                }}>
                  <div style={{
                    fontSize: "12px",
                    color: "#0369a1",
                  }}>
                    📞 Telefone: {selectedChannel.phone}
                  </div>
                </div>
              )}

              <div style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                marginTop: "24px",
              }}>
                <button
                  onClick={handleCloseCreateModal}
                  style={{
                    padding: "10px 20px",
                    background: "#e8ecf1",
                    color: "#1a1a1a",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCreateConnection}
                  disabled={creatingConnection}
                  style={{
                    padding: "10px 20px",
                    background: creatingConnection ? "#cbd5e1" : "#3b82f6",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: 600,
                    cursor: creatingConnection ? "not-allowed" : "pointer",
                  }}
                >
                  {creatingConnection ? "Criando..." : "Criar Conexão"}
                </button>
              </div>
            </div>
          </div>
        )}

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </MainContainer>
  );
};

export default NotificameHub;
